import React, { useState, useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import Details from '../../../components/AddOrEditLoanFile/Details';
import Borrowers from '../../../components/AddOrEditLoanFile/Borrowers';

import Documents from '../../../components/AddOrEditLoanFile/Documents';
import EditDocument from '../../../components/AddOrEditLoanFile/Documents/EditDocument';
import Underwriting from '../../../components/AddOrEditLoanFile/Underwriting';

import { useUiStore } from '../../../store';
import LoanFileService from '../../../services/LoanFileService';

const createSecondarySidebarLinks = (id) => {
  const middlePart = id ? `${id}` : 'create';
  const isDisabled = middlePart === 'create';

  return [
    {
      title: 'Details',
      url: `/loan-files/${middlePart}/details`,
    },
    {
      title: 'Borrowers',
      url: `/loan-files/${middlePart}/borrowers`,
      disabled: isDisabled,
    },
    {
      title: 'Documents',
      url: `/loan-files/${middlePart}/documents`,
      disabled: isDisabled,
    },
    {
      title: 'Underwriting',
      url: `/loan-files/${middlePart}/underwriting`,
      disabled: isDisabled,
    },
  ];
};

export default function AddOrEditLoanFilePage({ section }) {
  const [isAddBorrowerFormVisible, setIsAddBorrowerFormVisible] =
    useState(false);

  const { id, documentId } = useParams();
  const isEdit = !!id && id !== 'create';

  const setSecondarySidebarLinks = useUiStore(
    (state) => state.setSecondarySidebarLinks
  );

  const { data: loanFile, refetch } = useQuery({
    queryKey: ['loanFile', id],
    queryFn: () => LoanFileService.getLoanFile(id),
    enabled: isEdit,
  });

  const {
    data: bankStatementCollections,
    refetch: refetchBankStatementCollections,
  } = useQuery({
    queryKey: ['loanFileBankStatementCollections', id],
    queryFn: () => LoanFileService.getLoanFileBankStatementCollections(id),
    enabled: isEdit,
  });

  const { mutate: updateLoanFile } = useMutation({
    mutationFn: (formValues) =>
      LoanFileService.updateLoanFile({ id, formValues }),
    onSuccess: () => {
      refetch();
    },
    onError: (error) => {
      console.log(error);
    },
  });

  // Override the secondary sidebar links with the ones specific to the AddOrEditLoanFilePage
  useEffect(() => {
    const secondarySidebarLinks = createSecondarySidebarLinks(id);

    setSecondarySidebarLinks(secondarySidebarLinks);

    return () => {
      setSecondarySidebarLinks([]);
    };
  }, [id, setSecondarySidebarLinks]);

  return (
    <>
      {section === 'details' && (
        <Details
          loanFile={loanFile}
          isEdit={isEdit}
          loanFileId={id}
          updateLoanFile={updateLoanFile}
        />
      )}
      {section === 'borrowers' && (
        <Borrowers
          loanFileId={id}
          isAddBorrowerFormVisible={isAddBorrowerFormVisible}
          setIsAddBorrowerFormVisible={setIsAddBorrowerFormVisible}
          isEdit={isEdit}
        />
      )}
      {section === 'documents' && (
        <>
          {documentId ? (
            <EditDocument loanFileId={id} loanFile={loanFile} />
          ) : (
            <Documents loanFileId={id} isEdit={isEdit} loanFile={loanFile} />
          )}
        </>
      )}
      {section === 'underwriting' && (
        <Underwriting
          loanFileId={id}
          isEdit={isEdit}
          loanFile={loanFile}
          bankStatementCollections={bankStatementCollections}
          refetch={refetchBankStatementCollections}
        />
      )}
    </>
  );
}
